import {
  type FC, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ModalProps } from '~/modal/modalType.enum';
import { useCheckBoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { userSettingsSetAllowEditingBoundaryGroups } from '~/store/userData/settings/userDataSettings.actionCreators';
import { useUserSettingsIsBoundaryEditAllowed } from '~/store/userData/settings/userDataSettings.selectors';
import { useBoundaryGroupListingData } from '../hooks/useBoundaryGroupListingData';
import { useBoundaryGroupRemove } from '../hooks/useBoundaryGroupRemove';
import { useBoundaryGroupRename } from '../hooks/useBoundaryGroupRename';
import { BoundaryGroupsListingComponent } from './boundaryGroupsListing.component';

const SHOW_DELETE_UNUSED = false; // internal dev feature for cleaning testing accounts

type BoundaryGroupsListingProps = ModalProps;

export const BoundaryGroupsListingContainer: FC<BoundaryGroupsListingProps> = (props) => {
  const { customBoundaryGroups, isLoading: isBoundaryGroupListingLoading, getBoundaryGroups } = useBoundaryGroupListingData();
  const { removeBoundaryGroup, isLoading: isBoundaryGroupRemoveLoading } = useBoundaryGroupRemove();
  const { renameBoundaryGroup, isLoading: isBoundaryGroupRenameLoading } = useBoundaryGroupRename();
  const clientId = useClientIdSelector();
  const isUserTerritoriesEditAllowed = useUserSettingsIsBoundaryEditAllowed(clientId);
  const { checkBoundaryGroupPermissions } = useCheckBoundaryGroupPermissions();
  const [t] = useTranslation();

  const updateName = useCallback((itemId: number, newName: string) => {
    if (clientId === null) {
      return;
    }

    renameBoundaryGroup(itemId, newName, () => {
      getBoundaryGroups(clientId);
    });
  }, [clientId, renameBoundaryGroup, getBoundaryGroups]);

  const onRemoveBoundaryGroup = useCallback((boundaryGroupId: number) => {
    const boundaryGroup = customBoundaryGroups.find(group => group.id === boundaryGroupId);

    if (clientId === null || !boundaryGroup) {
      return;
    }

    removeBoundaryGroup(boundaryGroup.id, boundaryGroup.usedOnMaps.length, () => {
      getBoundaryGroups(clientId);
    });
  }, [removeBoundaryGroup, getBoundaryGroups, clientId, customBoundaryGroups]);

  const items = useMemo(() => {
    return customBoundaryGroups.map(group => {
      return {
        name: group.name,
        id: group.id,
        mapsCount: group.usedOnMaps.length,
        boundaryGroupPermissions: checkBoundaryGroupPermissions(group.id),
      };
    });
  }, [checkBoundaryGroupPermissions, customBoundaryGroups]);

  useEffect(() => {
    if (clientId === null) {
      return;
    }

    getBoundaryGroups(clientId);
  }, [clientId, getBoundaryGroups]);

  const onRemoveUnusedGroups = useMemo(() =>
    SHOW_DELETE_UNUSED ? () => {
      for (const group of customBoundaryGroups) {
        if (group.usedOnMaps.length === 0) {
          removeBoundaryGroup(group.id, 0);
        }
      }
    } : undefined
  , [customBoundaryGroups, removeBoundaryGroup]);

  const dispatch = useDispatch();
  const onEditAllowedChange = useCallback((allowEditing: boolean) => {
    if (clientId === null) {
      return;
    }

    dispatch(userSettingsSetAllowEditingBoundaryGroups(
      clientId,
      allowEditing,
    ));
  }, [clientId, dispatch]);

  return (
    <BoundaryGroupsListingComponent
      items={items}
      onClose={props.onClose}
      isOpen={props.isOpen}
      onEditName={updateName}
      isLoading={isBoundaryGroupListingLoading || isBoundaryGroupRemoveLoading || isBoundaryGroupRenameLoading}
      caption={t('Territory Groups')}
      isUserTerritoriesEditAllowed={isUserTerritoriesEditAllowed}
      onEditAllowedChange={onEditAllowedChange}
      onRemove={onRemoveBoundaryGroup}
      onRemoveUnused={onRemoveUnusedGroups}
    />
  );
};
